/**
 * Missing Units: every WRO that came in short, where its units actually went (found later, mixed
 * up with another size, never shipped by the supplier, or lost by ShipBob), what we claimed, and
 * what ShipBob credited. Claims are filed from here through the claims dialog. The billing check
 * compares each PO's supplier invoice and packing list (read from the purchasing inbox) with what
 * we ordered and what arrived, so we only pay for units that were shipped.
 */

import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  LinearProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InventoryService, {
  ClaimsOutboxEmail, LedgerStage, MissingUnitsLedger, MissingUnitsLine, MissingUnitsWro, PurchaseOrderCheck, ReconciliationFlag, ReconciliationLine,
  ShortfallCategory, ShortfallEvidence, SupplierDocumentType,
} from "services/InventoryService";
import WroClaimsDialog, { isValidEmail, parseEmails } from "shared/components/dialogs/WroClaimsDialog";
import { getActorName } from "shared/hooks/useActorName";

const PERIODS = { "90d": 90, "12m": 365, all: null } as const;
type Period = keyof typeof PERIODS;

const CATEGORY: Record<ShortfallCategory, { label: string; color: string; help: string }> = {
  claimable: { label: "Lost by ShipBob", color: "#c62828", help: "Not found, not mixed up, not short-shipped: ShipBob's to credit" },
  recovered: { label: "Found later", color: "#2e7d32", help: "Received on a found-units (URO) WRO" },
  recovery_pending: { label: "Being found", color: "#689f38", help: "On a found-units WRO ShipBob hasn't finished receiving" },
  mix_up: { label: "Size/colour mix-up", color: "#6a1b9a", help: "A sibling size or colour came in over by as much" },
  po_balanced: { label: "PO made whole", color: "#00838f", help: "The PO line received its full quantity across its WROs" },
  not_shipped: { label: "Never shipped", color: "#ef6c00", help: "Nothing (or most of a big line) arrived: most likely a supplier short shipment — confirm on the packing list" },
  not_claimable: { label: "Ruled out", color: "#757575", help: "Ruled out by a person (see the review note)" },
};
const CATEGORY_ORDER: ShortfallCategory[] = ["claimable", "not_shipped", "recovered", "recovery_pending", "mix_up", "po_balanced", "not_claimable"];

const STAGE: Record<LedgerStage, { label: string; color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" }> = {
  to_claim: { label: "To claim", color: "error" },
  waiting: { label: "Waiting", color: "info" },
  claimed: { label: "Claimed", color: "warning" },
  credited: { label: "Credited", color: "success" },
  denied: { label: "Denied", color: "default" },
  dismissed: { label: "Dismissed", color: "default" },
  nothing_to_claim: { label: "Nothing to claim", color: "default" },
};

const FLAG: Record<ReconciliationFlag, { label: string; color: string; help: string }> = {
  billed_not_shipped: { label: "Billed, not shipped", color: "#c62828", help: "The invoice charges for units the packing list doesn't show shipped: ask the supplier for a credit" },
  billed_over_order: { label: "Billed over order", color: "#c62828", help: "The invoice charges for more than our PO ordered" },
  not_on_po: { label: "Not on our PO", color: "#c62828", help: "On the supplier's documents but not on our PO" },
  billed_not_received: { label: "Billed, not received", color: "#ef6c00", help: "Billed for more than arrived, and no packing list yet to say whether the supplier or ShipBob is short" },
  shipped_not_received: { label: "Shipped, not received", color: "#ef6c00", help: "The packing list shows them shipped but they never arrived: ShipBob's to credit" },
  arrived_as_other_size: { label: "Arrived as other size", color: "#6a1b9a", help: "Arrived as a sibling size or colour instead: a mix-up, nothing owed" },
  short_shipped: { label: "Short-shipped", color: "#757575", help: "The supplier shipped less than ordered: fine as long as we weren't billed for them" },
};
const FLAG_ORDER: ReconciliationFlag[] = ["billed_not_shipped", "billed_over_order", "not_on_po", "billed_not_received", "shipped_not_received", "arrived_as_other_size", "short_shipped"];
const DOCUMENT_LABEL: Record<SupplierDocumentType, string> = { invoice: "Invoice", packing_list: "Packing list" };

const money = (value: number | null | undefined) =>
  value == null ? "—" : value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const units = (value: number) => value.toLocaleString("en-US");
const shipbobWroUrl = (wroId: string) => `https://web.shipbob.com/app/merchant/#/inventory/receiving/${wroId}/summary/`;

function evidenceText(evidence: ShortfallEvidence): string {
  if (evidence.recoveryWroId && evidence.siblingSku) return `${evidence.units} found as ${evidence.siblingSku} on WRO ${evidence.recoveryWroId} (“${evidence.recoveryWroName}”)`;
  if (evidence.recoveryWroId) return `${evidence.units} on WRO ${evidence.recoveryWroId} (“${evidence.recoveryWroName}”)`;
  if (evidence.siblingSku) return `${evidence.units} — ${evidence.siblingSku} came in over`;
  if (evidence.poNumbers) return `${evidence.units} — PO ${evidence.poNumbers.join(", ")} received in full`;
  if (evidence.reason === "packing_list") return `${evidence.units} — the packing list shows ${units(evidence.shipped ?? 0)} shipped`;
  if (evidence.reason === "none_received") return `${evidence.units} — none of the line arrived`;
  if (evidence.reason === "mostly_missing") return `${evidence.units} — most of the line never arrived`;
  return `${evidence.units}`;
}

function SplitChips({ breakdown }: { breakdown: Record<ShortfallCategory, number> }) {
  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {CATEGORY_ORDER.filter(category => breakdown[category] > 0).map(category => (
        <Tooltip key={category} title={CATEGORY[category].help}>
          <Chip
            size="small"
            label={`${units(breakdown[category])} ${CATEGORY[category].label.toLowerCase()}`}
            sx={{ borderColor: CATEGORY[category].color, color: CATEGORY[category].color, fontWeight: 500 }}
            variant="outlined"
          />
        </Tooltip>
      ))}
    </Box>
  );
}

/** Opens a supplier PDF through a short-lived signed link */
function DocumentChip({ service, id, label }: { service: InventoryService; id: number; label: string }) {
  const [isOpening, setIsOpening] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const open = async (event: React.MouseEvent) => {
    event.stopPropagation();
    // Open the tab inside the click so the popup blocker allows it, then point it at the PDF
    const tab = window.open("", "_blank");
    setIsOpening(true);
    setErrorMessage(null);
    try {
      const { url } = await service.getSupplierDocumentUrl(id);
      if (tab) tab.location.href = url; else window.location.href = url;
    } catch (error: any) {
      tab?.close();
      setErrorMessage(error.message);
    } finally {
      setIsOpening(false);
    }
  };
  return (
    <Tooltip title={errorMessage ?? "Open the PDF"}>
      <Chip size="small" variant="outlined" label={label} onClick={open} color={errorMessage ? "error" : "default"}
        icon={isOpening ? <CircularProgress size={12} /> : <OpenInNewIcon sx={{ fontSize: 14 }} />} />
    </Tooltip>
  );
}

function FlagChips({ flags }: { flags: ReconciliationLine["flags"] }) {
  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {FLAG_ORDER.flatMap(flag => flags.filter(f => f.flag === flag)).map(({ flag, units: count, value }) => (
        <Tooltip key={flag} title={FLAG[flag].help}>
          <Chip size="small" variant="outlined" label={`${units(count)} ${FLAG[flag].label.toLowerCase()}${value != null ? ` · ${money(value)}` : ""}`}
            sx={{ borderColor: FLAG[flag].color, color: FLAG[flag].color, fontWeight: 500 }} />
        </Tooltip>
      ))}
    </Box>
  );
}

function Tile({ title, value, detail, color }: { title: string; value: string; detail?: string; color?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150, flex: "1 1 150px" }}>
      <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>{title}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 600, color }}>{value}</Typography>
      {detail && <Typography variant="caption" sx={{ opacity: 0.7 }}>{detail}</Typography>}
    </Paper>
  );
}

export default function MissingUnitsPage(): JSX.Element {
  const service = useMemo(() => new InventoryService(), []);
  const [period, setPeriod] = useState<Period>("12m");
  const [ledger, setLedger] = useState<MissingUnitsLedger | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState<LedgerStage | "all">("all");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showBillingCheck, setShowBillingCheck] = useState(true);
  const [isClaimsDialogOpen, setIsClaimsDialogOpen] = useState(false);
  const [reviewing, setReviewing] = useState<{ wro: MissingUnitsWro; line: MissingUnitsLine } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const days = PERIODS[period];
      setLedger(await service.getMissingUnits(days == null ? undefined : moment().subtract(days, "days").format("YYYY-MM-DD")));
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [service, period]);

  useEffect(() => { load(); }, [load]);

  const toggle = (wroId: string) => setExpanded(previous => {
    const next = new Set(previous);
    if (next.has(wroId)) next.delete(wroId); else next.add(wroId);
    return next;
  });

  const stageCounts = (ledger?.wros ?? []).reduce<Record<string, number>>((counts, wro) => ({ ...counts, [wro.stage]: (counts[wro.stage] ?? 0) + 1 }), {});
  // A stage with no WROs left (after a period change or a review) has no chip: fall back to All
  const activeStage = stageFilter !== "all" && stageCounts[stageFilter] ? stageFilter : "all";
  const visibleWros = (ledger?.wros ?? [])
    .filter(wro => activeStage === "all" || wro.stage === activeStage)
    .sort((a, b) => (b.completedAt ?? "").localeCompare(a.completedAt ?? ""));
  const summary = ledger?.summary;

  return (
    <Box sx={{ p: 3, width: "100%", minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5">Missing Units</Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, maxWidth: 760 }}>
            Every WRO that came in short and where its units went. Only units ShipBob actually lost are claimable — found units,
            size mix-ups and supplier short shipments are not. Claims become due {ledger?.fileAfterDays ?? 3} days after ShipBob marks a WRO Completed.
            Each morning the claims agent drafts the claims that are due (once the PO's invoice and packing list are in) and asks Danican for
            any that are missing; every email waits below for your approval.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, next: Period | null) => next && setPeriod(next)}>
            <ToggleButton value="90d" sx={{ textTransform: "none" }}>90 days</ToggleButton>
            <ToggleButton value="12m" sx={{ textTransform: "none" }}>12 months</ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: "none" }}>All</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" color="error" disabled={!summary?.toClaim.wros} onClick={() => setIsClaimsDialogOpen(true)}>
            File claims{summary?.toClaim.wros ? ` (${summary.toClaim.wros})` : ""}
          </Button>
        </Box>
      </Box>

      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
      {successMessage && <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>{successMessage}</Alert>}
      {isLoading && !ledger && <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>}
      {isLoading && ledger && <LinearProgress sx={{ mb: 2 }} />}

      <ApprovalQueue service={service} onSent={message => { setSuccessMessage(message); load(); }} />

      {summary && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Where the missing units went</Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
            <Tile title="Missing on arrival" value={units(summary.missingUnits)} detail={`${summary.wros} WROs`} />
            <Tile title="Found later" value={units(summary.byCategory.recovered.units + summary.byCategory.recovery_pending.units)}
              detail={money(summary.byCategory.recovered.value + summary.byCategory.recovery_pending.value)} color={CATEGORY.recovered.color} />
            <Tile title="Never shipped (supplier)" value={units(summary.byCategory.not_shipped.units)}
              detail={`${money(summary.byCategory.not_shipped.value)} · confirm on packing list`} color={CATEGORY.not_shipped.color} />
            <Tile title="Mix-ups / PO made whole" value={units(summary.byCategory.mix_up.units + summary.byCategory.po_balanced.units)}
              detail={money(summary.byCategory.mix_up.value + summary.byCategory.po_balanced.value)} color={CATEGORY.mix_up.color} />
            <Tile title="Lost by ShipBob" value={units(summary.byCategory.claimable.units)}
              detail={`${money(summary.byCategory.claimable.value)}${summary.byCategory.claimable.unpricedUnits ? ` + ${summary.byCategory.claimable.unpricedUnits} unpriced` : ""}`}
              color={CATEGORY.claimable.color} />
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Claims and refunds</Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
            <Tile title="To claim now" value={money(summary.toClaim.value)} detail={`${summary.toClaim.wros} WROs`} color={CATEGORY.claimable.color} />
            <Tile title="Waiting" value={String(summary.waiting)} detail="WROs within the first days, or found units arriving" />
            <Tile title="Claimed" value={money(summary.claimed)} />
            <Tile title="Credited by ShipBob" value={money(summary.credited)} detail="from ShipBob's billing" color="#2e7d32" />
            <Tile title="Awaiting ShipBob" value={money(summary.outstanding.value)} detail={`${summary.outstanding.claims} open claims`} />
            <Tile title="Denied" value={money(summary.denied.value)} detail={`${summary.denied.claims} claims`} />
          </Box>

          {ledger && (
            <BillingCheck
              purchaseOrders={ledger.purchaseOrders}
              service={service}
              isOpen={showBillingCheck}
              onToggle={() => setShowBillingCheck(previous => !previous)}
            />
          )}

          {summary.creditedThenFound > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              ShipBob credited {summary.creditedThenFound} WRO{summary.creditedThenFound === 1 ? "" : "s"} whose units later turned up
              (marked below). ShipBob may ask for that money back.
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
            <Chip label={`All (${ledger?.wros.length ?? 0})`} onClick={() => setStageFilter("all")} color={activeStage === "all" ? "primary" : "default"} variant={activeStage === "all" ? "filled" : "outlined"} />
            {(Object.keys(STAGE) as LedgerStage[]).filter(stage => stageCounts[stage]).map(stage => (
              <Chip key={stage} label={`${STAGE[stage].label} (${stageCounts[stage]})`} onClick={() => setStageFilter(stage)}
                color={activeStage === stage ? STAGE[stage].color : "default"} variant={activeStage === stage ? "filled" : "outlined"} />
            ))}
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>WRO</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell align="right">Missing</TableCell>
                  <TableCell>Where they went</TableCell>
                  <TableCell align="right">Claimable</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Credited</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleWros.map(wro => {
                  const isOpen = expanded.has(wro.wroId);
                  const wroSplit = wro.lines.reduce<Record<ShortfallCategory, number>>((split, line) => {
                    const next = { ...split };
                    for (const category of CATEGORY_ORDER) next[category] = (next[category] ?? 0) + line.breakdown[category];
                    return next;
                  }, {} as Record<ShortfallCategory, number>);
                  return (
                    <Fragment key={wro.wroId}>
                      <TableRow hover sx={{ "& > td": { borderBottom: isOpen ? "none" : undefined }, cursor: "pointer" }} onClick={() => toggle(wro.wroId)}>
                        <TableCell sx={{ width: 32 }}>{isOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <strong>{wro.wroId}</strong>
                            <IconButton size="small" href={shipbobWroUrl(wro.wroId)} target="_blank" onClick={event => event.stopPropagation()}>
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                          <Typography variant="caption" sx={{ opacity: 0.7 }}>{wro.name || "—"}</Typography>
                        </TableCell>
                        <TableCell>
                          {wro.completedAt ? moment(wro.completedAt).format("MMM D, YYYY") : "—"}
                          {wro.daysSinceCompleted != null && <Typography variant="caption" sx={{ display: "block", opacity: 0.6 }}>{Math.floor(wro.daysSinceCompleted)}d ago</Typography>}
                        </TableCell>
                        <TableCell align="right">{units(wro.missingUnits)}</TableCell>
                        <TableCell><SplitChips breakdown={wroSplit} /></TableCell>
                        <TableCell align="right">{wro.claimableUnits > 0 ? money(wro.claimableCredit) : "—"}</TableCell>
                        <TableCell>
                          <Chip size="small" label={STAGE[wro.stage].label} color={STAGE[wro.stage].color} />
                          {wro.creditedThenFound && (
                            <Tooltip title="ShipBob credited this WRO, and some of its units later turned up">
                              <Chip size="small" label="Credited, then found" color="warning" variant="outlined" sx={{ ml: 0.5 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">{wro.creditedAmount > 0 ? money(wro.creditedAmount) : "—"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0, backgroundColor: "rgb(250, 250, 252)" }}>
                          <Collapse in={isOpen} unmountOnExit>
                            <WroDetail wro={wro} service={service} onReview={line => setReviewing({ wro, line })} onChanged={(message) => { setSuccessMessage(message); load(); }} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
                {visibleWros.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, opacity: 0.6 }}>No WROs here.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {ledger && ledger.otherCredits.length > 0 && (
            <Typography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.7 }}>
              Credited total includes {money(ledger.otherCredits.reduce((sum, credit) => sum + credit.amount, 0))} of receiving credits on WROs
              that no longer show a shortfall ({ledger.otherCredits.map(credit => credit.wroId).filter((id, i, all) => all.indexOf(id) === i).join(", ")}).
            </Typography>
          )}
        </>
      )}

      <WroClaimsDialog
        open={isClaimsDialogOpen}
        onClose={() => { setIsClaimsDialogOpen(false); load(); }}
        onResolved={(message) => setSuccessMessage(message)}
      />

      <ReviewDialog
        target={reviewing}
        categories={ledger?.reviewableCategories ?? []}
        onClose={() => setReviewing(null)}
        onSave={async (category, note) => {
          if (!reviewing) return;
          await service.reviewShortfallLine({ wroId: reviewing.wro.wroId, inventoryId: reviewing.line.inventoryId, category, note });
          setReviewing(null);
          setSuccessMessage(`WRO ${reviewing.wro.wroId}: ${reviewing.line.sku || reviewing.line.inventoryId} marked “${CATEGORY[category].label}”`);
          load();
        }}
      />
    </Box>
  );
}

function WroDetail({ wro, service, onReview, onChanged }: {
  wro: MissingUnitsWro;
  service: InventoryService;
  onReview: (line: MissingUnitsLine) => void;
  onChanged: (message: string) => void;
}) {
  const claim = wro.latestClaim;
  const [caseNumber, setCaseNumber] = useState(claim?.caseNumber ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const saveCaseNumber = async () => {
    if (!claim) return;
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await service.updateWroClaim(claim.id, { caseNumber: caseNumber.trim() || null });
      onChanged(`WRO ${wro.wroId}: case number saved`);
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ py: 2, px: 1 }}>
      <Table size="small" sx={{ mb: 2, "& td, & th": { borderColor: "rgba(0,0,0,0.06)" } }}>
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="right">Expected</TableCell>
            <TableCell align="right">Received</TableCell>
            <TableCell align="right">Missing</TableCell>
            <TableCell>Where they went</TableCell>
            <TableCell>Why</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {wro.lines.map(line => (
            <TableRow key={line.inventoryId}>
              <TableCell>
                <Typography variant="body2">
                  {line.sku || line.inventoryId}
                  {line.packingListChecked && (
                    <Tooltip title="What was shipped comes from the supplier's packing list, not a guess">
                      <Chip size="small" label="packing list" sx={{ ml: 0.75, height: 18, fontSize: 11 }} />
                    </Tooltip>
                  )}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>{line.name}{line.poNumbers.length ? ` · PO ${line.poNumbers.join(", ")}` : ""}</Typography>
              </TableCell>
              <TableCell align="right">{units(line.expected)}</TableCell>
              <TableCell align="right">{units(line.received)}</TableCell>
              <TableCell align="right">{units(line.missing)}</TableCell>
              <TableCell><SplitChips breakdown={line.breakdown} /></TableCell>
              <TableCell sx={{ maxWidth: 380 }}>
                {line.review ? (
                  <Typography variant="caption">Reviewed {moment(line.review.reviewedAt).format("MMM D")}: {line.review.note || CATEGORY[line.review.category].label}</Typography>
                ) : (
                  line.evidence.filter(evidence => evidence.category !== "claimable").map((evidence, i) => (
                    <Typography key={i} variant="caption" sx={{ display: "block" }}>{evidenceText(evidence)}</Typography>
                  ))
                )}
              </TableCell>
              <TableCell align="right">
                <Button size="small" sx={{ textTransform: "none" }} onClick={() => onReview(line)}>Review</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        <Box sx={{ minWidth: 320, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Claim</Typography>
          {!claim && <Typography variant="body2" sx={{ opacity: 0.7 }}>No claim filed.</Typography>}
          {claim && (
            <>
              <Typography variant="body2">
                {claim.status === "dismissed" ? "Dismissed" : `Filed ${moment(claim.claimedAt).format("MMM D, YYYY")} for ${money(claim.claimedAmount)}`}
                {claim.status !== "dismissed" && ` · ${claim.status.replace("_", " ")}`}
              </Typography>
              {claim.note && <Typography variant="caption" sx={{ display: "block", opacity: 0.7, whiteSpace: "pre-wrap" }}>{claim.note}</Typography>}
              {claim.status !== "dismissed" && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
                  <TextField size="small" label="ShipBob case #" value={caseNumber} onChange={event => setCaseNumber(event.target.value)} sx={{ width: 170 }} />
                  <Button size="small" variant="outlined" disabled={isSaving || caseNumber === (claim.caseNumber ?? "")} onClick={saveCaseNumber}>Save</Button>
                  <Tooltip title="From ShipBob's auto-reply (“ShipBob Case #…”). Their credit for this claim carries the same number, so the refund is matched automatically.">
                    <Typography variant="caption" sx={{ opacity: 0.6, cursor: "help" }}>why?</Typography>
                  </Tooltip>
                </Box>
              )}
              {errorMessage && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage}</Alert>}
              {claim.events.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {claim.events.map(event => (
                    <Typography key={event.id} variant="caption" sx={{ display: "block" }}>
                      {moment(event.at).format("MMM D")} — {event.event.replace("_", " ")}{event.status ? ` → ${event.status}` : ""}{event.amount != null ? ` (${money(event.amount)})` : ""}{event.detail ? `: ${event.detail}` : ""}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
        <Box sx={{ minWidth: 240, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Supplier documents</Typography>
          {wro.poNumbers.length === 0 && <Typography variant="body2" sx={{ opacity: 0.7 }}>Not linked to a PO.</Typography>}
          {wro.poNumbers.map(poNumber => {
            const documents = wro.documents.filter(doc => doc.poNumber === poNumber);
            const missing = (["invoice", "packing_list"] as SupplierDocumentType[]).filter(type => !documents.some(doc => doc.documentType === type));
            const request = wro.documentRequests.find(entry => entry.poNumber === poNumber);
            return (
              <Box key={poNumber} sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap", mb: 0.5 }}>
                <Typography variant="body2" sx={{ mr: 0.5 }}>PO {poNumber}:</Typography>
                {documents.map(doc => <DocumentChip key={doc.id} service={service} id={doc.id} label={DOCUMENT_LABEL[doc.documentType]} />)}
                {missing.length > 0 && (
                  <Typography variant="caption" sx={{ color: "#ef6c00" }}>no {missing.map(type => DOCUMENT_LABEL[type].toLowerCase()).join(" or ")} yet</Typography>
                )}
                {missing.length > 0 && request && (
                  <Typography variant="caption" sx={{ opacity: 0.7, width: "100%" }}>
                    Asked Danican {moment(request.firstAt).format("MMM D")}
                    {request.count > 1 ? ` · ${request.count - 1} reminder${request.count === 2 ? "" : "s"}, last ${moment(request.lastAt).format("MMM D")}` : ""}
                    {request.escalatedAt ? ` · flagged in Slack ${moment(request.escalatedAt).format("MMM D")}` : ""}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ minWidth: 280, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>ShipBob credits</Typography>
          {wro.credits.length === 0 && <Typography variant="body2" sx={{ opacity: 0.7 }}>None yet.</Typography>}
          {wro.credits.map(credit => (
            <Typography key={credit.transactionId} variant="body2">
              {moment(credit.date).format("MMM D, YYYY")} — {money(credit.amount)} · {credit.reason}{credit.caseNumber ? ` · case ${credit.caseNumber}` : ""}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/** The claims agent's drafted emails: each is reviewed, optionally edited, and sent by a person */
function ApprovalQueue({ service, onSent }: { service: InventoryService; onSent: (message: string) => void }) {
  const [emails, setEmails] = useState<ClaimsOutboxEmail[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErrorMessage(null);
    try {
      setEmails((await service.getClaimsOutbox()).emails);
    } catch (error: any) {
      setErrorMessage(error.message);
    }
  }, [service]);

  useEffect(() => { load(); }, [load]);

  if (errorMessage) return <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>;
  if (emails === null) return <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>Checking for emails waiting for approval…</Typography>;
  if (emails.length === 0) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#ef6c00" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Waiting for your approval ({emails.length})
      </Typography>
      <Typography variant="caption" sx={{ display: "block", opacity: 0.7, mb: 1.5 }}>
        Drafted by the claims agent. Check each one, edit anything you like, and send it. Nothing goes out until you click Send.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {emails.map(email => (
          <OutboxEmailCard key={email.key} email={email} service={service}
            onSent={message => { onSent(message); load(); }} />
        ))}
      </Box>
    </Paper>
  );
}

function OutboxEmailCard({ email, service, onSent }: { email: ClaimsOutboxEmail; service: InventoryService; onSent: (message: string) => void }) {
  const [to, setTo] = useState(email.to.join(", "));
  const [cc, setCc] = useState(email.cc.join(", "));
  const [subject, setSubject] = useState(email.subject);
  const [body, setBody] = useState(email.body);
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isEdited = to !== email.to.join(", ") || cc !== email.cc.join(", ") || subject !== email.subject || body !== email.body;

  const title = email.kind === "claim"
    ? `Claim to ShipBob — WRO ${email.wroId}${email.wroName ? ` (${email.wroName})` : ""} · ${units(email.units ?? 0)} units${email.creditTotal != null ? ` · ${money(email.creditTotal)}` : ""}`
    : `Ask Danican for documents — ${(email.requests ?? []).map(request => `PO ${request.poNumber}${request.attempt > 1 ? ` (reminder ${request.attempt - 1})` : ""}`).join(", ")}`;

  const send = async () => {
    const toList = parseEmails(to);
    const ccList = parseEmails(cc);
    if (toList.length === 0 || [...toList, ...ccList].some(address => !isValidEmail(address))) {
      setErrorMessage("Check the recipient addresses");
      return;
    }
    setIsSending(true);
    setErrorMessage(null);
    try {
      await service.sendClaimsOutboxEmail({ key: email.key, to: toList, cc: ccList, subject: subject.trim(), body, approvedBy: getActorName() || undefined });
      onSent(email.kind === "claim" ? `Claim for WRO ${email.wroId} sent to ShipBob` : "Document request sent to Danican");
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1, mb: 1 }}>
        <TextField size="small" label="To" value={to} onChange={event => setTo(event.target.value)} />
        <TextField size="small" label="Cc" value={cc} onChange={event => setCc(event.target.value)} />
      </Box>
      <TextField size="small" fullWidth label="Subject" value={subject} onChange={event => setSubject(event.target.value)} sx={{ mb: 1 }} />
      <TextField size="small" fullWidth multiline minRows={6} label="Message" value={body} onChange={event => setBody(event.target.value)} sx={{ mb: 1 }} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {email.attachments.length > 0 && <Typography variant="caption" sx={{ opacity: 0.7 }}>Attached:</Typography>}
        {email.attachments.map(doc => <DocumentChip key={doc.id} service={service} id={doc.id} label={doc.fileName} />)}
        <Box sx={{ flex: 1 }} />
        {isEdited && (
          <Button size="small" sx={{ textTransform: "none" }} disabled={isSending}
            onClick={() => { setTo(email.to.join(", ")); setCc(email.cc.join(", ")); setSubject(email.subject); setBody(email.body); }}>
            Undo my edits
          </Button>
        )}
        <Button variant="contained" size="small" onClick={send} disabled={isSending || !subject.trim() || !body.trim()}
          startIcon={isSending ? <CircularProgress size={14} color="inherit" /> : undefined}>
          {isSending ? "Sending…" : "Send"}
        </Button>
      </Box>
      {errorMessage && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage}</Alert>}
    </Paper>
  );
}

/** Per PO with supplier documents: billed vs shipped vs ordered vs arrived */
function BillingCheck({ purchaseOrders, service, isOpen, onToggle }: {
  purchaseOrders: PurchaseOrderCheck[];
  service: InventoryService;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const [expandedPo, setExpandedPo] = useState<string | null>(null);
  const sorted = [...purchaseOrders].sort((a, b) =>
    Number(b.needsAttention) - Number(a.needsAttention) || b.poNumber.localeCompare(a.poNumber, undefined, { numeric: true }));
  const attention = purchaseOrders.filter(po => po.needsAttention);
  const overbilled = purchaseOrders.reduce((sum, po) => sum + po.overbilled, 0);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", mb: 1 }} onClick={onToggle}>
        {isOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Supplier billing check</Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {purchaseOrders.length} PO{purchaseOrders.length === 1 ? "" : "s"} with documents
          {attention.length > 0 ? ` · ${attention.length} need${attention.length === 1 ? "s" : ""} a look` : " · all clear"}
          {overbilled > 0 ? ` · ${money(overbilled)} billed for units not shipped` : ""}
        </Typography>
      </Box>
      <Collapse in={isOpen} unmountOnExit>
        {purchaseOrders.length === 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.7, mb: 1 }}>
            No supplier invoices or packing lists yet. They arrive from the purchasing inbox as Danican emails them.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>PO</TableCell>
                  <TableCell>Documents</TableCell>
                  <TableCell align="right">Invoice total</TableCell>
                  <TableCell align="right">Billed, not shipped</TableCell>
                  <TableCell align="right">Shipped, not received</TableCell>
                  <TableCell align="right">Billed, not received</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map(po => {
                  const isPoOpen = expandedPo === po.poNumber;
                  const flagged = [...po.lines, ...po.extras].filter(line => line.flags.length > 0);
                  return (
                    <Fragment key={po.poNumber}>
                      <TableRow hover sx={{ cursor: "pointer", "& > td": { borderBottom: isPoOpen ? "none" : undefined } }}
                        onClick={() => setExpandedPo(isPoOpen ? null : po.poNumber)}>
                        <TableCell sx={{ width: 32 }}>{isPoOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}</TableCell>
                        <TableCell><strong>{po.poNumber}</strong></TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                            {po.invoice
                              ? <DocumentChip service={service} id={po.invoice.id} label={`Invoice${po.invoice.invoiceNumber ? ` ${po.invoice.invoiceNumber}` : ""}`} />
                              : <Typography variant="caption" sx={{ color: "#ef6c00" }}>no invoice</Typography>}
                            {po.packingList
                              ? <DocumentChip service={service} id={po.packingList.id} label="Packing list" />
                              : <Typography variant="caption" sx={{ color: "#ef6c00" }}>no packing list</Typography>}
                            {po.invoice && !po.invoice.verified && (
                              <Tooltip title="The invoice's lines and charges don't add up to its total as read: check the PDF before trusting these numbers">
                                <Chip size="small" color="warning" label="check reading" />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{po.invoice?.total != null ? money(po.invoice.total) : "—"}</TableCell>
                        <TableCell align="right" sx={{ color: po.overbilled > 0 ? FLAG.billed_not_shipped.color : undefined }}>{po.overbilled > 0 ? money(po.overbilled) : "—"}</TableCell>
                        <TableCell align="right" sx={{ color: po.shippedNotReceived > 0 ? FLAG.shipped_not_received.color : undefined }}>{po.shippedNotReceived > 0 ? money(po.shippedNotReceived) : "—"}</TableCell>
                        <TableCell align="right" sx={{ color: po.billedNotReceived > 0 ? FLAG.billed_not_received.color : undefined }}>{po.billedNotReceived > 0 ? money(po.billedNotReceived) : "—"}</TableCell>
                        <TableCell>
                          <Chip size="small" label={po.needsAttention ? "Needs a look" : "OK"} color={po.needsAttention ? "warning" : "success"} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0, backgroundColor: "rgb(250, 250, 252)" }}>
                          <Collapse in={isPoOpen} unmountOnExit>
                            <Box sx={{ py: 2, px: 1 }}>
                              {flagged.length === 0 ? (
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>Every line billed, shipped and received as ordered.</Typography>
                              ) : (
                                <Table size="small" sx={{ "& td, & th": { borderColor: "rgba(0,0,0,0.06)" } }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Item</TableCell>
                                      <TableCell align="right">Ordered</TableCell>
                                      <TableCell align="right">Shipped</TableCell>
                                      <TableCell align="right">Billed</TableCell>
                                      <TableCell align="right">Received</TableCell>
                                      <TableCell>What it means</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {flagged.map((line, i) => (
                                      <TableRow key={line.inventoryId ?? `${line.productNumber}-${i}`}>
                                        <TableCell>
                                          <Typography variant="body2">{line.sku || line.productNumber || "—"}</Typography>
                                          <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                            {line.name || line.description}{line.sku && line.productNumber ? ` · ${line.productNumber}` : ""}
                                          </Typography>
                                          {line.notes && line.notes.length > 0 && (
                                            <Typography variant="caption" sx={{ display: "block", fontStyle: "italic" }}>Supplier: {line.notes.join("; ")}</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell align="right">{line.ordered != null ? units(line.ordered) : "—"}</TableCell>
                                        <TableCell align="right">{line.shipped != null ? units(line.shipped) : "—"}</TableCell>
                                        <TableCell align="right">{line.billed != null ? units(line.billed) : "—"}</TableCell>
                                        <TableCell align="right">
                                          {line.received != null ? units(line.received) : "—"}
                                          {line.mixedUp ? <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>+{units(line.mixedUp)} as other size</Typography> : null}
                                        </TableCell>
                                        <TableCell><FlagChips flags={line.flags} /></TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Box>
  );
}

function ReviewDialog({ target, categories, onClose, onSave }: {
  target: { wro: MissingUnitsWro; line: MissingUnitsLine } | null;
  categories: ShortfallCategory[];
  onClose: () => void;
  onSave: (category: ShortfallCategory, note: string) => Promise<void>;
}) {
  // No preset: a Save without a choice would otherwise rule the line claimable, and the agent files it
  const [category, setCategory] = useState<ShortfallCategory | "">("");
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    setCategory(target.line.review?.category ?? "");
    setNote(target.line.review?.note ?? "");
    setErrorMessage(null);
  }, [target]);

  const save = async () => {
    if (!category) return;
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await onSave(category, note.trim());
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={target !== null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Review a short line</DialogTitle>
      <DialogContent>
        {target && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            WRO {target.wro.wroId} · {target.line.sku || target.line.inventoryId}: {units(target.line.missing)} missing.
            Your call replaces the automatic split for this line.
          </Typography>
        )}
        <TextField select fullWidth size="small" label="These units are" value={category} onChange={event => setCategory(event.target.value as ShortfallCategory)} sx={{ mb: 2 }}>
          {categories.map(option => <MenuItem key={option} value={option}>{CATEGORY[option].label} — {CATEGORY[option].help}</MenuItem>)}
        </TextField>
        <TextField fullWidth multiline minRows={2} size="small" label="Why (e.g. packing list shows all 10 shipped)" value={note} onChange={event => setNote(event.target.value)} />
        {errorMessage && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={isSaving || !category}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
